import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Plus, Users } from 'lucide-react';
import { GuestContactDialog } from './GuestContactDialog';
import { 
  guestContactService, 
  GuestContact, 
  CreateGuestContactData 
} from '@/services/guestContactService';
import { Guest } from '@/types/event';
import { toast } from 'sonner';

interface GuestContactSelectorProps {
  selectedGuests: Guest[];
  onGuestsChange: (guests: Guest[]) => void;
}

export const GuestContactSelector = ({ selectedGuests, onGuestsChange }: GuestContactSelectorProps) => {
  const [contacts, setContacts] = useState<GuestContact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<GuestContact[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContacts();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchQuery, selectedTags]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await guestContactService.getUserGuestContacts();
      setContacts(data);
      
      // Extract all unique tags
      const tags = new Set<string>();
      data.forEach(contact => {
        contact.tags?.forEach(tag => tags.add(tag));
      });
      setAllTags(Array.from(tags).sort());
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(contact =>
        contact.name.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query)
      );
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(contact =>
        selectedTags.some(tag => contact.tags?.includes(tag))
      );
    }

    setFilteredContacts(filtered);
  };

  const handleCreateContact = async (data: CreateGuestContactData) => {
    await guestContactService.createGuestContact(data);
    loadContacts();
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const isContactSelected = (contact: GuestContact) => {
    return selectedGuests.some(guest => 
      (guest.email && guest.email === contact.email) ||
      (guest.phone && guest.phone === contact.phone)
    );
  };

  const handleContactToggle = (contact: GuestContact, checked: boolean) => {
    if (checked) {
      // Add contact to selected guests
      const newGuest: Guest = {
        id: crypto.randomUUID(),
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        hasVoted: false,
        invitedAt: new Date().toISOString(),
      };
      onGuestsChange([...selectedGuests, newGuest]);
    } else {
      // Remove contact from selected guests
      const updatedGuests = selectedGuests.filter(guest =>
        !((guest.email && guest.email === contact.email) ||
          (guest.phone && guest.phone === contact.phone))
      );
      onGuestsChange(updatedGuests);
    }
  };

  const selectAllFromTag = (tag: string) => {
    const contactsWithTag = contacts.filter(contact => 
      contact.tags?.includes(tag) && !isContactSelected(contact)
    );
    
    const newGuests = contactsWithTag.map(contact => ({
      id: crypto.randomUUID(),
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      hasVoted: false,
      invitedAt: new Date().toISOString(),
    }));

    onGuestsChange([...selectedGuests, ...newGuests]);
  };

  if (loading) {
    return <div className="text-center py-4">Loading contacts...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Your Saved Contacts
          </CardTitle>
          <GuestContactDialog
            onSave={handleCreateContact}
            trigger={
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add New
              </Button>
            }
          />
        </div>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted-foreground mb-4">
              No saved contacts yet. Create your first contact to get started!
            </p>
            <GuestContactDialog
              onSave={handleCreateContact}
              trigger={
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Contact
                </Button>
              }
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Tag filters */}
            {allTags.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <Badge
                      key={tag}
                      variant={selectedTags.includes(tag) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 ml-2 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAllFromTag(tag);
                        }}
                      >
                        (+ all)
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Contacts list */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {filteredContacts.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No contacts match your filters
                </p>
              ) : (
                filteredContacts.map((contact) => {
                  const isSelected = isContactSelected(contact);
                  return (
                    <div
                      key={contact.id}
                      className="flex items-center space-x-3 p-2 rounded-lg border bg-background hover:bg-accent/50"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => 
                          handleContactToggle(contact, checked as boolean)
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{contact.name}</div>
                        <div className="text-sm text-muted-foreground truncate">
                          {contact.email || contact.phone}
                        </div>
                        {contact.tags && contact.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {contact.tags.map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};